<template>
  <div class="wallet-address flex-1 pa-5 d-flex flex-column">
    <div
      class="flex-1 d-flex flex-column justify-center p-relative"
      v-if="!connectError"
    >
      <div class="text--primary text-size-5 text-center">
        Choose address to interact with:
      </div>
      <div class="addresses mt-2">
        <v-radio-group
          v-model="addressSelected"
          :mandatory="false"
          dense
          hide-details
        >
          <v-radio
            :value="item.address"
            v-for="(item, idx) in accountAddresses"
            :key="item.address + idx"
            :label="item.address"
            color="primary"
            small
          ></v-radio>
          <v-radio
            value="_custom"
            label="Input HD derivation path"
            color="primary"
            small
          ></v-radio>
        </v-radio-group>
        <div
          v-if="addressSelected === '_custom'"
          class="mt-3 d-flex align-center"
        >
          <div style="width:180px">
            <v-text-field
              autofocus
              outlined
              single-line
              class="hd-path-input"
              :placeholder="customHDPath.value"
              :prefix="customHDPath.prefix"
              v-model="customHDPath.value"
              hide-details="auto"
            />
          </div>
          <div
            class="custom-address text-5 darkgray--text rounded-5 d-flex align-center px-2 ml-3 flex-1"
            style="height:40px"
          >
            {{ customHDPath.address }}
          </div>
        </div>
        <div class="d-flex justify-space-between mt-5">
          <v-icon medium color="primary" :disabled="!pageIndex" @click="getPrev"
            >mdi-chevron-left</v-icon
          >
          <v-icon medium color="primary" @click="getNext"
            >mdi-chevron-right</v-icon
          >
        </div>
        <c-loading v-if="loading" class="ml-n3 mr-n3"></c-loading>
      </div>
    </div>
    <div v-else class="text-center">
      <div class="error--text text-5">Error: {{ connectError.message }}</div>
      <div class="mt-1 text-5">
        Make sure your ledger is unlocked and
        <span class="text-capitalize">{{
          hdPath.ledgerAppName || hdPath.name
        }}</span>
        is open.
      </div>
      <div class="mt-8">
        <c-btn @click="connect">Retry</c-btn>
      </div>
    </div>
    <v-divider class="mt-5 mb-5"></v-divider>
    <div>
      <v-checkbox
        v-model="checkbox"
        label="I accept the Terms and Conditions to access my wallet."
        class="accept-cb"
      ></v-checkbox>
    </div>
    <div class="d-flex justify-end align-center mt-5">
      <div
        class="text-4 font-weight-medium gray--text clickable"
        @click="cancel"
      >
        Cancel
      </div>
      <c-btn
        @click="next"
        :disabled="!addressSelected || loading || !checkbox"
        class="ml-5"
        >Connect</c-btn
      >
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { debouncePromise, deriveHdPathStr } from "@/common/lib/utils";

const ACCOUNTS_PER_PAGE = 4;

export default {
  name: "WalletAddress",
  props: ["hdPath", "project"],
  data: () => ({
    checkbox: true,
    addressSelected: null,
    loading: true,
    pageIndex: 0,
    accountAddresses: Array(ACCOUNTS_PER_PAGE).fill({ address: "Loading..." }),
    connectError: null,
    customHDPath: {
      prefix: "",
      value: "",
      address: "",
      invalid: true,
    },
  }),
  computed: {
    ...mapGetters({
      activeBackend: "walletUtils/getActiveBackend",
      validBackend: "walletUtils/getValidBackend",
    }),
    coinName() {
      return this.project.config.network;
    },
    customHDPathStr() {
      return `${this.customHDPath.prefix}${this.customHDPath.value}`;
    },
  },
  methods: {
    getHdPathStr(accountNum, addressIdx = 0) {
      return deriveHdPathStr(this.hdPath.path, accountNum, addressIdx);
    },
    async reconnectWallet() {
      return this.$store.dispatch("walletUtils/importBackend", {
        coinName: this.coinName,
        skipFailsThreshold: true,
        appName: this.hdPath.app,
      });
    },
    async connect() {
      this.connectError = null;
      this.loading = true;
      this.$store.dispatch("walletUtils/setBackend", "ledger");
      try {
        await this.$store.dispatch("walletUtils/importBackend", {
          coinName: this.coinName,
          skipFailsThreshold: true,
          appName: this.hdPath.app,
        });
        if (this.validBackend && this.validBackend !== true) {
          throw new Error(this.validBackend);
        }
        await this.getAccounts(this.pageIndex);
      } catch (error) {
        this.$store.dispatch("walletUtils/cleanUpBackend");
        this.loading = false;
        this.connectError = error;
      }
    },
    async getAccounts(pageIndex = 0) {
      this.loading = true;
      try {
        this.accountAddresses = await Promise.all(
          Array(ACCOUNTS_PER_PAGE)
            .fill(0)
            .map(async (_, idx) => {
              const hdPathStr = this.getHdPathStr(
                0,
                pageIndex * ACCOUNTS_PER_PAGE + idx
              );
              const account = await this.activeBackend.getAccount({
                hdPath: hdPathStr,
              });
              const { address: addr } = await account.getAddress();
              return {
                hdPath: hdPathStr,
                address: addr,
              };
            })
        );
      } catch (error) {
        if (error.name === "TransportStatusError") {
          await this.reconnectWallet();
          await this.getAccounts(pageIndex);
        } else {
          this.connectError = error;
          throw new Error(error.message || error);
        }
      } finally {
        this.loading = false;
      }
    },
    getPrev() {
      this.pageIndex--;
      this.getAccounts(this.pageIndex);
    },
    getNext() {
      this.pageIndex++;
      this.getAccounts(this.pageIndex);
    },
    cancel() {
      this.loading = false;
      this.$emit("cancel");
    },
    next() {
      if (this.addressSelected === "_custom") {
        if (this.customHDPath.invalid) {
          this.connectError = new Error(this.customHDPath.address);
          this.addressSelected = "";
          this.customHDPath.address = "";
          this.setDefaultCustomHdPath();
          return;
        }
        this.$emit("next", this.customHDPath.address, {
          hdPath: this.customHDPathStr,
        });
      } else {
        const hdPath = this.accountAddresses.find(
          ac => ac.address === this.addressSelected
        ).hdPath;
        this.$emit("next", this.addressSelected, { hdPath });
      }
    },
    async onCustomHdPathChange() {
      try {
        this.customHDPath.address = "Loading";
        this.customHDPath.invalid = true;
        const account = await this.activeBackend.getAccount({
          hdPath: this.customHDPathStr,
        });
        const addr = await account.getAddress();
        this.customHDPath.address = addr;
        this.customHDPath.invalid = false;
      } catch (error) {
        this.customHDPath.address = error.message;
      }
    },
    setDefaultCustomHdPath() {
      const defaultPath = this.getHdPathStr(0, 0);
      const idx = defaultPath.indexOf("/", 6) + 1;
      this.customHDPath.prefix = defaultPath.slice(0, idx);
      this.customHDPath.value = defaultPath.slice(idx);
    },
  },
  async created() {
    this.onCustomHdPathChangeDebounced = debouncePromise(
      this.onCustomHdPathChange.bind(this),
      500
    );
  },
  async mounted() {
    await this.connect();
    this.setDefaultCustomHdPath();
  },
  watch: {
    customHDPathStr: {
      immediate: false,
      handler(value) {
        const address = this.accountAddresses.find(
          item => item.hdPath === value
        )?.address;
        if (address) {
          this.customHDPath.address = address;
        } else {
          this.onCustomHdPathChangeDebounced();
        }
      },
    },
  },
};
</script>
